import { z } from 'zod';
import { WritePolicy } from './catalog-trust.ts';

export const EventStatus = z.enum(['unreviewed', 'active', 'deprecated', 'blocked', 'test_only']);
export type EventStatus = z.infer<typeof EventStatus>;

/** Allowed status changes of a registered event (BB-27). */
export const EVENT_STATUS_TRANSITIONS: Record<EventStatus, readonly EventStatus[]> = {
  unreviewed: ['active', 'test_only', 'blocked'],
  active: ['deprecated', 'blocked'],
  deprecated: ['active', 'blocked'],
  blocked: ['active'],
  test_only: ['active', 'blocked'],
};
export const EventProducer = z.enum(['sdk', 'server', 'both', 'unknown']);
export const PropertyDataType = z.enum(['string', 'number', 'boolean', 'object', 'array', 'null']);
export const PropertySensitivity = z.enum(['none', 'pii', 'secret']);

export const EventListQuery = z.object({
  query: z.string().trim().max(255).optional(),
  status: EventStatus.optional(),
  producer: EventProducer.optional(),
  tag: z.string().trim().max(64).optional(),
  cursor: z.string().max(255).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type EventListQuery = z.infer<typeof EventListQuery>;

const EventNameField = z.string().trim().min(1).max(255).refine((n) => !n.startsWith('$'), 'names starting with $ are reserved');

export const CreateEventBody = z.object({
  name: EventNameField,
  description: z.string().max(2000).nullable().optional(),
  producer: EventProducer.optional(),
  owner: z.string().max(120).nullable().optional(),
  properties: z.array(z.object({ path: z.string().min(1).max(255), dataType: PropertyDataType, sensitivity: PropertySensitivity.optional() })).max(256).optional(),
});
export type CreateEventBody = z.infer<typeof CreateEventBody>;

export const UpdateEventBody = z
  .object({
    description: z.string().max(2000).nullable().optional(),
    owner: z.string().max(120).nullable().optional(),
    producer: EventProducer.optional(),
    status: EventStatus.optional(),
    deprecationNote: z.string().max(500).nullable().optional(),
    replacedBy: z.string().max(255).nullable().optional(),
    writePolicy: WritePolicy.optional(),
    /** Required when the write policy is loosened (BB-33). */
    reason: z.string().trim().max(500).optional(),
  })
  .refine((b) => Object.keys(b).some((k) => k !== 'reason'), 'nothing to change');
export type UpdateEventBody = z.infer<typeof UpdateEventBody>;

/** At most this many properties per environment can be used in segment filters (BB-42, as in Braze). */
export const SEGMENTABLE_PROPERTIES_LIMIT = 20;

export const UpdatePropertyBody = z
  .object({ description: z.string().max(1000).nullable().optional(), sensitivity: PropertySensitivity.optional(), segmentable: z.boolean().optional() })
  .refine((b) => Object.keys(b).length > 0, 'nothing to change');
export type UpdatePropertyBody = z.infer<typeof UpdatePropertyBody>;

export const NoticeQuery = z.object({
  kind: z.enum(['unknown_event', 'event_property_type_changed', 'blocked_event_received', 'test_event_in_prod']).optional(),
  acknowledged: z.enum(['true', 'false']).default('false'),
});
export type NoticeQuery = z.infer<typeof NoticeQuery>;

export const PickerQuery = z.object({ query: z.string().trim().max(255).optional() });
